import server from 'common/utils/server';
import { temporaryHashId } from 'common/utils/temporaryHashId';

export const enum AttachmentType {
  FILE = 'fileAttachments',
  METRIC = 'metricAttachments',
  SOLICITATION = 'solicitationAttachments',
  REVIEW = 'qaReviewAttachments',
}

const initialState = {
  attachmentCreationInProgress: false,
  metricAttachments: [],
  solicitationAttachments: [],
  qaReviewAttachments: [],
  fileAttachments: [],
  urlAttachments: [],
};

export const coachingSessionAttachments = (state = initialState, action) => {
  switch (action.type) {
    case 'coaching_session_attachment/STARTED_ATTACHMENT_CREATION': {
      return {
        ...state,
        attachmentCreationInProgress: true,
      };
    }
    case 'coaching_session_attachment/FINISHED_ATTACHMENT_CREATION': {
      return {
        ...state,
        attachmentCreationInProgress: false,
      };
    }
    case 'LOADED_COACHING_SESSION': {
      const {
        metricAttachments,
        solicitationAttachments,
        qaReviewAttachments,
        fileAttachments,
        urlAttachments,
      } = action;
      return {
        ...state,
        metricAttachments,
        solicitationAttachments,
        qaReviewAttachments,
        fileAttachments,
        urlAttachments,
      };
    }

    case 'CREATE_SESSION_ATTACHMENT': {
      const { attachmentListName, entity } = action;

      const metricConfigAttachmentEntity = {
        id: temporaryHashId(),
        notes: '',
        ...entity,
      };

      return {
        ...state,
        [attachmentListName]: [
          ...state[attachmentListName],
          metricConfigAttachmentEntity,
        ],
      };
    }

    case 'REMOVE_SESSION_ATTACHMENT': {
      const { attachmentListName, attachmentId } = action;

      return {
        ...state,
        [attachmentListName]: state[attachmentListName].filter(
          a => a.id !== attachmentId
        ),
      };
    }

    case 'UPDATE_ATTACHMENT_NOTES': {
      const { attachmentListName, attachmentId, newValue } = action;

      const newAttachmentsList = state[attachmentListName].map(attachment => {
        return attachment.id === attachmentId
          ? { ...attachment, notes: newValue }
          : attachment;
      });

      return {
        ...state,
        [attachmentListName]: newAttachmentsList,
      };
    }

    case 'UPDATE_SESSION_ATTACHMENT': {
      const { attachmentListName, attachmentId, newValue } = action;

      const newAttachmentsList = state[attachmentListName].map(attachment => {
        return attachment.id === attachmentId
          ? { ...attachment, ...newValue }
          : attachment;
      });

      return {
        ...state,
        [attachmentListName]: newAttachmentsList,
      };
    }

    default:
      return state;
  }
};

export function formatMetricAttachment(metricAttachment) {
  return {
    id: metricAttachment.id,
    notes: metricAttachment.notes,
    title: metricAttachment.title,
    rawValue: metricAttachment.raw_value,
    displayValue: metricAttachment.display_value,
    rawDiff: metricAttachment.raw_diff,
    displayDiff: metricAttachment.display_diff,
    startDate: metricAttachment.start_date,
    endDate: metricAttachment.end_date,
    employeeIds: metricAttachment.employee_ids,
    timeSeries: metricAttachment.time_series,
    dates: metricAttachment.dates,
    higherIsBetter: metricAttachment.higher_is_better,
    updatedAt: metricAttachment.updated_at,
  };
}

export function formatSolicitationAttachment(solicitationAttachment) {
  return {
    id: solicitationAttachment.id,
    notes: solicitationAttachment.notes,
    followUpId: solicitationAttachment.follow_up_id,
    solicitationId: solicitationAttachment.solicitation_id,
    stars: solicitationAttachment.stars,
  };
}

export function formatQaReviewAttachment(qaReviewAttachment) {
  return {
    id: qaReviewAttachment.id,
    notes: qaReviewAttachment.notes,
    followUpId: qaReviewAttachment.follow_up_id,
    qaReviewId: qaReviewAttachment.qa_review_id,
    score: qaReviewAttachment.score,
  };
}

export function formatFileAttachment(fileAttachment) {
  return {
    id: fileAttachment.id,
    notes: fileAttachment.notes,
    coachingSessionFileId: fileAttachment.coaching_session_file_id,
    createdAt: fileAttachment.created_at,
  };
}

export function formatUrlAttachment(urlAttachment) {
  return {
    id: urlAttachment.id,
    notes: urlAttachment.notes,
    name: urlAttachment.name,
    url: urlAttachment.url,
    createdAt: urlAttachment.created_at,
  };
}

export const createMetricAttachment =
  (coachingSessionId, metricAttachmentEntity) => dispatch => {
    const temporaryAttachmentId = temporaryHashId();
    dispatch({
      type: 'coaching_session_attachment/STARTED_ATTACHMENT_CREATION',
    });
    dispatch({
      type: 'CREATE_SESSION_ATTACHMENT',
      attachmentListName: 'metricAttachments',
      entity: {
        ...metricAttachmentEntity,
        id: temporaryAttachmentId,
        loading: true,
      },
    });
    return server
      .post(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/metric_attachments`,
        {
          title: metricAttachmentEntity.title,
          raw_value: metricAttachmentEntity.rawValue,
          display_value: metricAttachmentEntity.displayValue,
          raw_diff: metricAttachmentEntity.rawDiff,
          display_diff: metricAttachmentEntity.displayDiff,
          start_date: metricAttachmentEntity.startDate,
          end_date: metricAttachmentEntity.endDate,
          employee_ids: metricAttachmentEntity.employeeIds,
          time_series: metricAttachmentEntity.timeSeries,
          dates: metricAttachmentEntity.dates,
          higher_is_better: metricAttachmentEntity.higherIsBetter,
        }
      )
      .then(response => {
        dispatch({
          type: 'UPDATE_SESSION_ATTACHMENT',
          attachmentListName: 'metricAttachments',
          attachmentId: temporaryAttachmentId,
          newValue: {
            id: response.id,
            loading: false,
            updatedAt: response.updated_at,
          },
        });
      })
      .catch(() => {
        dispatch({
          type: 'REMOVE_SESSION_ATTACHMENT',
          attachmentListName: 'metricAttachments',
          attachmentId: temporaryAttachmentId,
        });
      })
      .finally(() => {
        dispatch({
          type: 'coaching_session_attachment/FINISHED_ATTACHMENT_CREATION',
        });
      });
  };

export const createSolicitationAttachment =
  (coachingSessionId, solicitationAttachment) => dispatch => {
    const temporaryAttachmentId = temporaryHashId();
    dispatch({
      type: 'CREATE_SESSION_ATTACHMENT',
      attachmentListName: 'solicitationAttachments',
      entity: {
        ...solicitationAttachment,
        id: temporaryAttachmentId,
        loading: true,
      },
    });
    server
      .post(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/solicitation_attachments`,
        {
          follow_up_id: solicitationAttachment.followUpId,
          solicitation_id: solicitationAttachment.solicitationId,
          stars: solicitationAttachment.stars,
        }
      )
      .then(response => {
        dispatch({
          type: 'UPDATE_SESSION_ATTACHMENT',
          attachmentListName: 'solicitationAttachments',
          attachmentId: temporaryAttachmentId,
          newValue: { id: response.id, loading: false },
        });
      })
      .catch(() => {
        dispatch({
          type: 'REMOVE_SESSION_ATTACHMENT',
          attachmentListName: 'solicitationAttachments',
          attachmentId: temporaryAttachmentId,
        });
      });
  };

export const createQaReviewAttachment =
  (coachingSessionId, qaAttachmentEntity) => dispatch => {
    const temporaryAttachmentId = temporaryHashId();
    dispatch({
      type: 'CREATE_SESSION_ATTACHMENT',
      attachmentListName: 'qaReviewAttachments',
      entity: {
        ...qaAttachmentEntity,
        id: temporaryAttachmentId,
        loading: true,
      },
    });
    server
      .post(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/qa_review_attachments`,
        {
          follow_up_id: qaAttachmentEntity.followUpId,
          qa_review_id: qaAttachmentEntity.qaReviewId,
          score: qaAttachmentEntity.score,
        }
      )
      .then(response => {
        dispatch({
          type: 'UPDATE_SESSION_ATTACHMENT',
          attachmentListName: 'qaReviewAttachments',
          attachmentId: temporaryAttachmentId,
          newValue: { id: response.id, loading: false },
        });
      })
      .catch(() => {
        dispatch({
          type: 'REMOVE_SESSION_ATTACHMENT',
          attachmentListName: 'qaReviewAttachments',
          attachmentId: temporaryAttachmentId,
        });
      });
  };

export const createFileSessionAttachment =
  (coachingSessionId, coachingSessionFileId, temporaryAttachmentId) =>
  dispatch => {
    return server
      .post(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/file_attachments`,
        {
          coaching_session_file_id: coachingSessionFileId,
        }
      )
      .then(response => {
        dispatch({
          type: 'CREATE_SESSION_ATTACHMENT',
          attachmentListName: 'fileAttachments',
          entity: {
            id: response.id,
            coachingSessionFileId: response.coaching_session_file_id,
            createdAt: response.created_at,
          },
        });
      })
      .catch(() => {
        dispatch({
          type: 'REMOVE_SESSION_ATTACHMENT',
          attachmentListName: 'fileAttachments',
          attachmentId: temporaryAttachmentId,
        });
      });
  };

export const removeMetricAttachment =
  (coachingSessionId, metricAttachmentId) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'metricAttachments',
      attachmentId: metricAttachmentId,
      newValue: { loading: true },
    });
    server
      .delete(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/metric_attachments/${metricAttachmentId}`
      )
      .then(() => {
        dispatch({
          type: 'REMOVE_SESSION_ATTACHMENT',
          attachmentListName: 'metricAttachments',
          attachmentId: metricAttachmentId,
        });
      });
  };

export const removeSolicitationAttachment =
  (coachingSessionId, solicitationAttachmentId) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'solicitationAttachments',
      attachmentId: solicitationAttachmentId,
      newValue: { loading: true },
    });
    server
      .delete(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/solicitation_attachments/${solicitationAttachmentId}`
      )
      .then(() => {
        dispatch({
          type: 'REMOVE_SESSION_ATTACHMENT',
          attachmentListName: 'solicitationAttachments',
          attachmentId: solicitationAttachmentId,
        });
      });
  };

export const removeQaReviewAttachment =
  (coachingSessionId, qaReviewAttachmentId) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'qaReviewAttachments',
      attachmentId: qaReviewAttachmentId,
      newValue: { loading: true },
    });
    server
      .delete(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/qa_review_attachments/${qaReviewAttachmentId}`
      )
      .then(() => {
        dispatch({
          type: 'REMOVE_SESSION_ATTACHMENT',
          attachmentListName: 'qaReviewAttachments',
          attachmentId: qaReviewAttachmentId,
        });
      });
  };

export const removeFileAttachment =
  (coachingSessionId, fileAttachmentId) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'fileAttachments',
      attachmentId: fileAttachmentId,
      newValue: { loading: true },
    });
    server
      .delete(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/file_attachments/${fileAttachmentId}`
      )
      .then(() => {
        dispatch({
          type: 'REMOVE_SESSION_ATTACHMENT',
          attachmentListName: 'fileAttachments',
          attachmentId: fileAttachmentId,
        });
      });
  };

  export const removeUrlAttachment =
  (coachingSessionId, urlAttachmentId) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'urlAttachments',
      attachmentId: urlAttachmentId,
      newValue: { loading: true },
    });
    server
      .delete(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/url_attachments/${urlAttachmentId}`
      )
      .then(() => {
        dispatch({
          type: 'REMOVE_SESSION_ATTACHMENT',
          attachmentListName: 'urlAttachments',
          attachmentId: urlAttachmentId,
        });
      });
  };

export const genericEditNotesAction =
  (coachingSessionId, attachmentId, attachmentListName, value) => dispatch => {
    dispatch({
      type: 'UPDATE_ATTACHMENT_NOTES',
      attachmentListName,
      attachmentId,
      newValue: value,
    });
  };

export const updateMetricAttachment =
  (coachingSessionId, newAttachment) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'metricAttachments',
      attachmentId: newAttachment.id,
      newValue: { loading: true },
    });
    server
      .put(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/metric_attachments/${newAttachment.id}`,
        {
          notes: newAttachment.notes,
        }
      )
      .then(() => {
        dispatch({
          type: 'UPDATE_SESSION_ATTACHMENT',
          attachmentListName: 'metricAttachments',
          attachmentId: newAttachment.id,
          newValue: { loading: false },
        });
      });
  };

export const updateSolicitationAttachment =
  (coachingSessionId, newAttachment) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'solicitationAttachments',
      attachmentId: newAttachment.id,
      newValue: { loading: true },
    });
    server
      .put(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/solicitation_attachments/${newAttachment.id}`,
        {
          notes: newAttachment.notes,
        }
      )
      .then(() => {
        dispatch({
          type: 'UPDATE_SESSION_ATTACHMENT',
          attachmentListName: 'solicitationAttachments',
          attachmentId: newAttachment.id,
          newValue: { loading: false },
        });
      });
  };

export const updateQaReviewAttachment =
  (coachingSessionId, newAttachment) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'qaReviewAttachments',
      attachmentId: newAttachment.id,
      newValue: { loading: true },
    });
    server
      .put(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/qa_review_attachments/${newAttachment.id}`,
        {
          notes: newAttachment.notes,
        }
      )
      .then(() => {
        dispatch({
          type: 'UPDATE_SESSION_ATTACHMENT',
          attachmentListName: 'qaReviewAttachments',
          attachmentId: newAttachment.id,
          newValue: { loading: false },
        });
      });
  };

export const updateFileAttachment =
  (coachingSessionId, newAttachment) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'fileAttachments',
      attachmentId: newAttachment.id,
      newValue: { ...newAttachment, loading: true },
    });
    server
      .put(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/file_attachments/${newAttachment.id}`,
        {
          notes: newAttachment.notes,
        }
      )
      .then(() => {
        dispatch({
          type: 'UPDATE_SESSION_ATTACHMENT',
          attachmentListName: 'fileAttachments',
          attachmentId: newAttachment.id,
          newValue: { loading: false },
        });
      });
  };

  export const updateUrlAttachment =
  (coachingSessionId, newAttachment) => dispatch => {
    dispatch({
      type: 'UPDATE_SESSION_ATTACHMENT',
      attachmentListName: 'urlAttachments',
      attachmentId: newAttachment.id,
      newValue: { ...newAttachment, loading: true },
    });
    server
      .put(
        `/api/v1/coaching/coaching_session/${coachingSessionId}/url_attachments/${newAttachment.id}`,
        {
          notes: newAttachment.notes,
        }
      )
      .then(() => {
        dispatch({
          type: 'UPDATE_SESSION_ATTACHMENT',
          attachmentListName: 'urlAttachments',
          attachmentId: newAttachment.id,
          newValue: { ...newAttachment, loading: false },
        });
      });
  };
